import { useMemo, useState } from "react";
import { archiveAlbums } from "@/mocks/gallery";
import AlbumCard from "@/components/feature/AlbumCard";
import SectionHeading from "@/components/base/SectionHeading";

export default function Archive() {
  const [query, setQuery] = useState("");
  const [tag, setTag] = useState("全部");
  const [expanded, setExpanded] = useState(false);

  const allTags = useMemo(() => {
    const set = new Set<string>();
    archiveAlbums.forEach((album) => album.tags.forEach((t) => set.add(t)));
    return ["全部", ...Array.from(set)];
  }, []);

  const filtered = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    return archiveAlbums.filter((album) => {
      const matchTag = tag === "全部" || album.tags.includes(tag);
      const matchKeyword =
        keyword === "" ||
        album.title.toLowerCase().includes(keyword) ||
        album.model.toLowerCase().includes(keyword) ||
        album.category.toLowerCase().includes(keyword) ||
        album.tags.some((t) => t.toLowerCase().includes(keyword));
      return matchTag && matchKeyword;
    });
  }, [query, tag]);

  const visible = expanded ? filtered : filtered.slice(0, 6);

  const resetFilter = () => {
    setQuery("");
    setTag("全部");
    setExpanded(false);
  };

  return (
    <section
      id="archive"
      className="scroll-mt-24 bg-foreground-950 px-4 py-16 md:px-8 md:py-24"
    >
      <div className="mx-auto max-w-6xl">
        <SectionHeading
          tone="dark"
          eyebrow="HD Archive"
          title="高清珍藏库"
          desc="输入关键词或点选标签，直接在本页筛选珍藏合集；标题、模特、类别与标签都参与匹配，结果全部来自页面已有内容。"
        />

        <div className="mx-auto mt-10 max-w-3xl">
          <div className="flex items-center gap-2 rounded-md border border-background-50/20 bg-background-50/10 px-4 py-3 backdrop-blur-sm">
            <span className="flex h-5 w-5 items-center justify-center text-background-200">
              <i className="ri-search-line text-base" />
            </span>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜索合集名称、女主或类别，例如：胶片、海边、温瑜"
              aria-label="搜索珍藏合集"
              className="w-full bg-transparent text-sm text-background-50 outline-none placeholder:text-background-200/60"
            />
            {query ? (
              <button
                type="button"
                onClick={() => setQuery("")}
                aria-label="清空搜索"
                className="flex h-5 w-5 items-center justify-center text-background-200 hover:text-background-50"
              >
                <i className="ri-close-circle-line text-base" />
              </button>
            ) : null}
          </div>

          <div className="mt-4 flex flex-wrap justify-center gap-2">
            {allTags.map((t) => {
              const isActive = tag === t;
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTag(t)}
                  aria-pressed={isActive}
                  className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors md:text-sm ${
                    isActive
                      ? "bg-accent-500 text-foreground-950"
                      : "border border-background-50/20 bg-background-50/5 text-background-200 hover:border-accent-400/60 hover:text-background-50"
                  }`}
                >
                  {t}
                </button>
              );
            })}
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-background-200 md:text-sm">
          共找到 <span className="font-semibold text-accent-300">{filtered.length}</span> 组合集
          {tag !== "全部" ? ` · 标签「${tag}」` : ""}
          {query.trim() ? ` · 关键词「${query.trim()}」` : ""}
        </p>

        {visible.length > 0 ? (
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {visible.map((album) => (
              <AlbumCard key={album.id} album={album} />
            ))}
          </div>
        ) : (
          <div className="mt-10 flex flex-col items-center gap-4 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-50/10 text-background-200">
              <i className="ri-inbox-line text-2xl" />
            </span>
            <p className="text-sm text-background-200">
              没有匹配的合集，换个关键词或标签试试。
            </p>
            <button
              type="button"
              onClick={resetFilter}
              className="whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              重置筛选
            </button>
          </div>
        )}

        {!expanded && filtered.length > 6 ? (
          <div className="mt-10 text-center">
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/25 bg-background-50/10 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/20"
            >
              <i className="ri-add-line" />
              加载更多合集（{filtered.length - 6}）
            </button>
          </div>
        ) : null}
      </div>
    </section>
  );
}